/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2022, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 */
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { ErrorService } from '../../../common/error.service';
import { BULB_BUSY_COVER_SERVICE } from '../../../app/angularjs.providers';

/**
 * Supported media blocks.
 */
export const MEDIA = ['embed', 'image', 'audio', 'video', 'attachedFile', 'imageCarousel'];

/**
 * Placeholder blocks.
 */
const PLACEHOLDERS = ['bulbTranscoder', 'bulbUploadPlaceholder'];

/**
 * Service for handle undo/redo.
 */
@Injectable({
  providedIn: 'root',
})
export class UndoRedoService {
  /**
   * Subject which will be having updated content block.
   */
  contentBlockSubject : Subject<any> = new Subject<any>();

  /**
   * Subject which will be used to indicate undo/redo action is in process.
   */
  undoRedoSubject : BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /**
   * Subject which will be used to indicate undo/redo action is in process.
   */
  undoRedoPlaceholderProgress = false;

  /**
   * Undo or redo.
   * @private
   */
  undoOrRedo: string = null;

  /**
   * Undo or redo. This will be use to call command internally to ignore placeholder/transcoding process during undo/redo.
   * @private
   */
  action: string = null;

  /**
   * Id of block
   * @private
   */
  private blockId = null;

  /**
   * Constructor
   *
   * @param errorService Our custom error service.
   * @param http Service to interact with backend api.
   * @param BulbBusyCoverService Show loader during backend api communication.
   */
  constructor(
    private errorService: ErrorService,
    private http : HttpClient,
    @Inject(BULB_BUSY_COVER_SERVICE) private BulbBusyCoverService,
  ) { }

  /**
   * Configure change event after undo/redo.
   *
   * @param editor Editor.
   * @param unit Unit of page
   */
  configureChangeOnUndoRedo(editor, unit) {
    editor.model.document.on('change:data', (event, data) => {
      if (data.isUndo && this.undoOrRedo !== null) {
        const changes = editor.model.document.differ.getChanges({ includeChangesInGraveyard: false }).reverse();
        let includedPlaceholder = false;
        for (const entry of changes) {
          if (entry.name === 'video') {
            const videoType = entry.attributes?.get('video-type');
            if (!videoType) {
              editor.execute(editor.bulb.service.undoRedo.action);
              editor.execute(editor.bulb.service.undoRedo.action);
              return;
            }
          }
          includedPlaceholder = this.handleTrancodeUndoRedo(entry, includedPlaceholder);
          if (MEDIA.includes(entry.name) && (entry.type === 'insert' || entry.type === 'remove')) {
            if (this.blockId !== null && (entry.attributes.get('bulb-block-id') === this.blockId)) {
              this.handleUndoRedo(unit, this.blockId, editor, this.undoOrRedo, entry.type);
            } else {
              const item = entry.position.nodeAfter;

              for (const imageElement of this.getImagesFromChangeItem(editor, item)) {
              // Check if the image element still has upload id.
                if (MEDIA.includes(imageElement.name)) {
                  const blockId = imageElement.getAttribute('bulb-block-id') || entry.attributes.get('bulb-block-id');
                  this.handleUndoRedo(unit, blockId, editor, this.undoOrRedo, entry.type);
                }
              }
            }
          }
        }
      }
      if (data.isUndo) {
        setTimeout(() => editor.editing.view.scrollToTheSelection(), 0);
      }
      this.undoOrRedo = null;
      this.blockId = null;
    });
  }

  /**
   * Configure Undo redo handling.
   *
   * @param unit Unit of page
   * @param element Element to perform undo/redo
   * @param eventInfo Event
   * @param action Undo/redo
   */
  configureUndoRedo(unit, element, eventInfo, action) {
    this.action = action;
    this.undoOrRedo = null;
    if (!element || !MEDIA.includes(element.name) || !unit) {
      if (!element || PLACEHOLDERS.includes(element.name)) {
        this.undoOrRedo = action;
      } else {
        return;
      }
    }

    if (element && MEDIA.includes(element.name)) {
      this.blockId = element.getAttribute('bulb-block-id');
      const operation = eventInfo.source._stack[eventInfo.source._stack.length - 1].batch.operations[0];

      if (operation.key) {
        if (operation.key === 'src' || operation.key === 'bulb-block-version') {
          this.handleUndoRedo(unit, element.getAttribute('bulb-block-id'), eventInfo.source.editor, action);
        } else if (operation.key === 'bulb-id') {
          this.undoOrRedo = action;
        }
      } else {
        this.undoOrRedo = action;
      }
    }
  }

  /**
   * Handle undo / redo operation for block which is handled by CKE default.
   *
   * @param unit Unit of page.
   * @param blockId Element or id of element.
   * @param editor Undo/Redo event
   * @param action undo or redo
   * @param customAction Action for insert or removed on undo/redo.
   */
  async handleUndoRedo(unit, blockId, editor, action, customAction = undefined) {
    const version = unit?.contentBlocks[blockId]?.version || 0;
    const hideBusy : () => void = this.BulbBusyCoverService.showBusy();

    try {
      this.undoRedoSubject.next(true);

      const contentBlock = await this.getContentBlock(unit.id, blockId, version, action, customAction);
      if (!customAction || customAction && customAction === 'insert') {
        if (contentBlock) {
          if (Object.keys(contentBlock).length !== 0) {
            editor.bulb.page.unit.contentBlocks[blockId] = contentBlock;
            this.contentBlockSubject.next(contentBlock);
          }
        } else {
          editor.bulb.page.unit.contentBlocks[blockId] = null;
        }
      }
    } catch (error) {
      this.errorService.handleException(`Error ${action} on block ${blockId} of unit : ${unit.id} `
        + `with error => ${error}`);
    } finally {
      hideBusy();
      this.undoRedoSubject.next(false);
    }
  }

  /**
   * Get content block after undo/redo.
   *
   * @param unitId Id of unit
   * @param blockId Id of block
   * @param version Version of block
   * @param operation undo/redo operation
   * @param action Check if element getting inserted/removed
   */
  getContentBlock(unitId, blockId, version, operation, action): Promise<any> {
    let url = `units/${unitId}/block/${blockId}/${operation}/${version}`;
    if (action !== undefined) {
      url = `${url}?action=${action}`;
    }
    return this.http.patch(url, {}).toPromise();
  }

  /**
   * Get Image element from changes.
   *
   * @param editor Editor
   * @param item Changed item
   */
  getImagesFromChangeItem(editor, item) {
    return Array.from(editor.model.createRangeOn(item))
      .map((value : any) => value.item);
  }

  /**
   * Stop transcoding media again on undo/redo.
   *
   * @param entry Batch for operation
   * @param includedPlaceholder True if placeholder already inserted
   * @private
   */
  private handleTrancodeUndoRedo(entry: any, includedPlaceholder: boolean) : boolean {
    if (PLACEHOLDERS.includes(entry.name) && !includedPlaceholder) {
      this.undoRedoPlaceholderProgress = (entry.type === 'insert');
      return (entry.type === 'insert');
    }
    return includedPlaceholder;
  }
}
